import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Activity, Target } from "lucide-react";

import { getColorFromThreshold } from "./conversion/chart-utils";
import { calculateRevenueAggregates } from "./performance-calculations";

const ACHIEVEMENT_THRESHOLDS = {
  GOOD: 1.0,
  MODERATE: 2.0,
};

const AVAILABILITY_THRESHOLDS = {
  GOOD: 95,
  MODERATE: 75,
};

interface AvailabilityStats {
  avgAvailability: number;
}

interface PerformanceMetricCardsProps {
  revenueData: any[];
  primaryStats: AvailabilityStats;
  secondaryStats: AvailabilityStats;
}

export default function PerformanceMetricCards({
  revenueData,
  primaryStats,
  secondaryStats,
}: PerformanceMetricCardsProps) {
  const aggregatedRow = calculateRevenueAggregates(revenueData);
  const cpiAchievement = aggregatedRow?.cpi_achievement || 0;
  const cpaAchievement = aggregatedRow?.secondary_cpa_achievement || 0;
  const hasTargetCpa = (aggregatedRow?.secondary_ap_target_cpx_eur || 0) > 0;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">CPI Achievement</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getColorFromThreshold(cpiAchievement, ACHIEVEMENT_THRESHOLDS, true)}`}>
            {cpiAchievement.toLocaleString("en-US", {
              style: "percent",
            })}
          </div>
          <p className="text-xs text-muted-foreground">Performance vs target</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">CPA Achievement</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {hasTargetCpa ? (
            <>
              <div
                className={`text-2xl font-bold ${getColorFromThreshold(cpaAchievement, ACHIEVEMENT_THRESHOLDS, true)}`}
              >
                {cpaAchievement.toLocaleString("en-US", {
                  style: "percent",
                })}
              </div>
              <p className="text-xs text-muted-foreground">Performance vs target</p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">No target CPA set</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Bid Context Availability (AP)</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${getColorFromThreshold(primaryStats.avgAvailability, AVAILABILITY_THRESHOLDS)}`}
          >
            {primaryStats.avgAvailability.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground">Bid context available</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Bid Context Availability (2nd AP)</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {hasTargetCpa ? (
            <>
              <div
                className={`text-2xl font-bold ${getColorFromThreshold(secondaryStats.avgAvailability, AVAILABILITY_THRESHOLDS)}`}
              >
                {secondaryStats.avgAvailability.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground">Bid context available</p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground">No target CPA set</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
